import { ref } from "vue";
import { v4 as uuidv4 } from "uuid";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import { injectDimensionIds, parseRule } from "@/modules/rule_engine/parser";
import { RuleSetRule } from "@/modules/rule_engine/ruleEngineClasses";
import RuleModelVersionDTO = App.Data.RuleEngine.RuleModelVersionDTO;
import RuleSetDTO = App.Data.RuleEngine.Rules.RuleSetDTO;
import RuleSetVersionType = App.Enums.RuleSetVersionType;

export type SaveStrategy = "new" | "replace";

/**
 * Loading and saving of a rule model version.
 *
 * Shared by every builder shell so the load order, the change-detection
 * baseline and the save payload have exactly one implementation.
 */
export const useRuleModelVersion = (
	modelUuid: string,
	versionUuid: string,
	defaultVersionType: RuleSetVersionType
) => {
	const store = useRuleEnginedStore();
	const isInitialized = ref<boolean>(false);

	const loadDimensions = async () => {
		const response = await useApiClient("ruleEngine").getDimensions();

		if (response.isOk()) {
			store.ruleEngineState.ruleDimensions = response.value;
		}
	};

	const loadAbTests = async () => {
		const response = await useApiClient("testing").getABTests({ per_page: 100 });

		if (response.isOk()) {
			store.ruleEngineState.abTests = response.value.data;
		}
	};

	const loadVersion = async () => {
		store.ruleEngineState.isProcessingBEResponse = true;

		const response = await useApiClient("ruleEngine").showVersion(
			modelUuid,
			versionUuid
		);

		if (response.isOk()) {
			store.ruleEngineState.rawRuleModelVersion = response.value;
			store.collectRuleSetAiSummaries(response.value);
			store.ensureSelectedModelVersionTypeIsValid();

			// Wrapping each rule set in a RuleSetRule means every level of the model
			// has the same shape, so one component can render any depth.
			response.value.rule_sets.forEach((rule_set) => {
				store.ruleEngineState.parsedModelTypes[rule_set.version_type] =
					parseRule<"rule_set">({
						uuid: uuidv4(),
						type: "rule_set",
						rule_set,
					});
			});

			// Start at the root of whichever version type is selected. This used to
			// hardcode "per_hour", which is absent from models that don't define it.
			store.ruleEngineState.displayedGroupUuid =
				store.parsedModelType?.uuid ?? null;
		}

		// Baselines for change detection and undo — both need the parsed model.
		store.markModelAsSaved();
		store.beginHistory();
		store.ruleEngineState.isProcessingBEResponse = false;
	};

	/**
	 * @param onLoaded runs after the model is in the store but before the UI is
	 *                 marked ready, for consumer-specific data (todos, labels…).
	 */
	const initialize = async (onLoaded?: () => Promise<void> | void) => {
		store.resetState(defaultVersionType);

		await Promise.all([loadDimensions(), loadAbTests()]);
		await loadVersion();
		await onLoaded?.();

		isInitialized.value = true;
	};

	const saveAsVersion = async (saveStrategy: SaveStrategy) => {
		const newVersion = { ...store.activeRuleModelVersion };

		// AI summary fields aren't part of the editable RuleSet type (they're
		// background-job state, not something a save should carry) — the
		// backend ignores them on the way in and regenerates them after save.
		newVersion.rule_sets = Object.values(
			store.ruleEngineState.parsedModelTypes
		).map(
			(version: RuleSetRule): RuleSetDTO => ({
				...version.rule_set,
				ai_summary: null,
				ai_summary_status: null,
				ai_summary_generated_at: null,
			})
		);

		const response = await toastApiResult(
			useApiClient("ruleEngine").storeVersion(
				injectDimensionIds<RuleModelVersionDTO>(newVersion),
				newVersion.rule_model_uuid,
				saveStrategy
			)
		);

		if (response.isOk()) {
			// prevent prompting 'unsaved changes' after saving changes
			store.markModelAsSaved();
		}

		return response;
	};

	return { isInitialized, initialize, loadVersion, saveAsVersion };
};